import type {CSSProperties} from 'react';
import {radius, space} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeShareLook = 'share-app' | 'share-redact' | 'share-redact-on';

const SHARE_LOOKS = new Set<string>(['share-app', 'share-redact', 'share-redact-on']);
export const isWedgeShareLook = (look: string): look is WedgeShareLook => SHARE_LOOKS.has(look);

/* Released brief Share sheet (the sheet that Share opens on a brief): two kinds of native control. The app tile is one of the Messages, Mail,
   Slack and Copy link tiles in the sheet's horizontal row: a bare 64px wide column with no fill and no border, a 6px gap and its children
   centered, in the Wedge font. It holds the 56px colored tile and the 11px label that the caller draws, and it never shrinks in the row.
   The Redact names toggle is a full-width left-aligned flex row with a 12px gap, 12px by 14px padding and a 12px radius. It has a hairline
   0.5px border: the elevated fill and the separator while the toggle is off, the soft accent tint and the accent while it is on. Its fill
   changes over 200ms on the Wedge ease. Each reads the exact legacy roles, in the declaration order of the source. The caller owns the
   glyphs, the labels, the switch drawn inside the toggle and the handlers.
   Neither look draws a transparent target, an anchor or a stacking context (`wedgeSettingsDrawsHit`). The tile is 64px wide and at least 73px
   tall (the 56px tile, the gap and one line of 11px type), and the toggle is as wide as the sheet and at least 52px tall (its padding twice and
   the 28px glyph disc), so a 44px region could only repeat the box and would square the 12px corners of the toggle. */
const TILE_WIDTH = 64, TILE_GAP = 6;
const TOGGLE_PADDING = '12px 14px', HAIRLINE = 0.5, TOGGLE_MS = 200;

export function wedgeShareStyle(look: WedgeShareLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'share-app') return {
    background: 'none', border: 0, cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: TILE_GAP,
    fontFamily: r('font'), flexShrink: 0, width: TILE_WIDTH,
  };
  const on = look === 'share-redact-on';
  return {
    width: '100%', display: 'flex', alignItems: 'center', gap: space[3],
    padding: TOGGLE_PADDING, borderRadius: radius.lg,
    background: r(on ? 'accentTint' : 'bgElev'),
    border: `${HAIRLINE}px solid ${r(on ? 'accent' : 'separator')}`,
    cursor: 'pointer', fontFamily: r('font'), textAlign: 'left',
    transition: `background ${TOGGLE_MS}ms ${r('ease')}`,
  };
}
